import { memo, useCallback, useMemo, useState } from 'react';
import { DndContext, closestCorners, DragOverlay } from '@dnd-kit/core';
import PropTypes from 'prop-types';
import CandidateColumn from './CandidateColumn';
import CandidateCard from './CandidateCard';
import useCandidatePipelineStore from '@stores/candidatePipelineStore';
import { KanbanSkeleton } from '@components/loaders/SkeletonLoaders';
import { WidgetErrors } from '@components/error/WidgetErrors';

const STATUS_COLUMNS = Object.freeze([
    { id: 'Pending', title: 'All Candidates' },
    { id: 'Review', title: 'Review' },
    { id: 'Rejected', title: 'Rejected' },
    { id: 'Accepted', title: 'Accepted' },
]);

const findContainer = (containers, id) => {
    if (!id) return null;
    const container = containers.find(
        container => container.id === id || container.candidates.some(card => card.id === id),
    );
    return container?.id || null;
};

const CandidateKanbanBoard = memo(({ filteredCandidates = [], error, loading }) => {
    // Manage active id internally instead of through props
    const [activeId, setActiveId] = useState(null);
    const updateCandidateStatus = useCandidatePipelineStore(state => state.updateCandidateStatus);

    // Handle drag start
    const handleDragStart = useCallback(event => {
        const { active } = event;
        setActiveId(active.id);
    }, []);

    // Memoize column candidates calculation
    const getColumnCandidates = useCallback(
        status => {
            if (!Array.isArray(filteredCandidates)) {
                console.error('Invalid filteredCandidates:', filteredCandidates);
                return [];
            }
            return filteredCandidates.filter(
                candidate => candidate.status.toLowerCase() === status.toLowerCase(),
            );
        },
        [filteredCandidates],
    );

    // Memoize status columns with their candidates
    const statusColumnsWithCandidates = useMemo(
        () =>
            STATUS_COLUMNS.map(col => ({
                id: col.id,
                candidates: getColumnCandidates(col.id),
            })),
        [getColumnCandidates],
    );

    // Memoize active candidate
    const activeCandidate = useMemo(
        () => filteredCandidates?.find(c => c.id === activeId),
        [filteredCandidates, activeId],
    );

    const handleDragEnd = useCallback(
        async event => {
            const { active, over } = event;
            if (!over) {
                setActiveId(null);
                return;
            }

            const activeContainer = findContainer(statusColumnsWithCandidates, active.id);
            const overContainer = findContainer(statusColumnsWithCandidates, over.id);

            if (!activeContainer || !overContainer || activeContainer === overContainer) {
                setActiveId(null);
                return;
            }

            try {
                await updateCandidateStatus(active.id, overContainer);
            } catch (error) {
                console.error('Error updating candidate status:', error);
            } finally {
                setActiveId(null);
            }
        },
        [statusColumnsWithCandidates, updateCandidateStatus],
    );

    if (loading) {
        return <KanbanSkeleton columns={3} cardsPerColumn={2} />;
    }

    if (error) {
        return <WidgetErrors error={error} title="" />;
    }

    return (
        <DndContext
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="flex-1 p-4 bg-white rounded-xl">
                <div className="bg-slate-100 rounded-xl p-2 overflow-auto scrollbar-thin">
                    <div className="grid grid-cols-4 gap-4 h-full min-w-[1400px]">
                        {STATUS_COLUMNS.map(({ id, title }) => (
                            <CandidateColumn
                                key={`column-${id}`}
                                status={id}
                                title={title}
                                candidates={getColumnCandidates(id)}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <DragOverlay>
                {activeId && activeCandidate ? (
                    <CandidateCard candidate={activeCandidate} isDragging />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
});

CandidateKanbanBoard.displayName = 'CandidateKanbanBoard';

CandidateKanbanBoard.propTypes = {
    error: PropTypes.string,
    loading: PropTypes.bool,
    filteredCandidates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            candidate_name: PropTypes.string.isRequired,
            status: PropTypes.oneOf(['Pending', 'Review', 'Rejected', 'Accepted']).isRequired,
        }),
    ).isRequired,
};

export default CandidateKanbanBoard;
